import React from 'react';
import Head from 'next/head';

const toJson = (type: string, jsonld: any) => {
  let data = jsonld;

  if (Array.isArray(data) && data.length === 1) {
    data = { ...jsonld[0] };
  }
  const jsonLdObject = Array.isArray(data)
    ? data.map((item) => formatObjectForSchema(type, item))
    : formatObjectForSchema(type, data);

  return {
    __html: JSON.stringify(jsonLdObject, safeJsonLdReplacer),
  };
};

const formatObjectForSchema = (type: string, jsonld: any) => {
  const { id = undefined } = jsonld;
  const updated = {
    ...(id ? { '@id': jsonld.id } : {}),
    ...jsonld,
  };
  delete updated.id;

  return {
    '@context': 'https://schema.org',
    '@type': type,
    ...updated,
  };
};

type JsonValueScalar = string | boolean | number;
type JsonValue = JsonValueScalar | Array<JsonValue> | { [key: string]: JsonValue };
type JsonReplacer = (_: string, value: JsonValue) => JsonValue | undefined;

const ESCAPE_ENTITIES = Object.freeze({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&apos;',
});
const ESCAPE_REGEX = new RegExp(`[${Object.keys(ESCAPE_ENTITIES).join('')}]`, 'g');
const ESCAPE_REPLACER = (t: string): string => ESCAPE_ENTITIES[t as keyof typeof ESCAPE_ENTITIES];

const safeJsonLdReplacer: JsonReplacer = (() => {
  return (_: string, value: JsonValue): JsonValue | undefined => {
    switch (typeof value) {
      case 'object':
        if (value === null) {
          return undefined;
        }
        return value;
      case 'number':
      case 'boolean':
      case 'bigint':
        return value;
      case 'string':
        return value.replace(ESCAPE_REGEX, ESCAPE_REPLACER);
      default: {
        isNever(value);
        return undefined;
      }
    }
  };
})();

function isNever(_: never): void {}

export const stringify = (data: any) => JSON.stringify(data, safeJsonLdReplacer);

export interface JsonLdProps {
  type?: string;
  scriptId?: string;
  dataArray?: any[];
  useAppDir?: boolean;
  [key: string]: any;
}

function JsonLd({
  type = 'Thing',
  keyOverride,
  scriptKey,
  scriptId = undefined,
  dataArray,
  useAppDir = false,
  ...rest
}: JsonLdProps & { scriptKey: string }) {
  const JsonLdScript = () => (
    <script
      type="application/ld+json"
      id={scriptId}
      data-testid={scriptId}
      dangerouslySetInnerHTML={toJson(type, dataArray === undefined ? { ...rest } : dataArray)}
      key={`jsonld-${scriptKey}${keyOverride ? `-${keyOverride}` : ''}`}
    />
  );

  if (useAppDir) {
    return <JsonLdScript />;
  }
  return <Head>{JsonLdScript()}</Head>;
}

export { JsonLd };
